import type { SupabaseClient } from '@supabase/supabase-js';
import { showToast, KF_LOGO_GOECOMMERCE } from '@vario-connect/frontend-design';
import {
  anlageFehlerText,
  formatVerbindungen,
  formatZugang,
  parseMcpStatus,
  validateZugangName,
  MCP_SERVER_URL,
  type McpVerbindung,
  type McpZugang,
} from '../../lib/mcp-access-flow.js';
import {
  createRevokeState,
  pressRevoke,
  revokeLabel,
  syncRevokeState,
  type RevokeState,
} from '../../lib/revoke-state.js';

interface McpSlotCtx {
  contentEl: HTMLElement;
  supabase: SupabaseClient;
  session: { token: string; tenantId: string; marketplaceKind?: string };
  supabaseUrl: string;
}

const TRENNEN_TEXTE = { normal: 'Trennen', scharf: 'Wirklich trennen?' };

/**
 * App-Tab „MCP" (/#/mcp, system.apps.{appId}.tabs): Zugang fuer KI-Assistenten
 * (Claude, ChatGPT). Vorlage: mcp-oauth-card.ts + renderMcpSlot der Kaufland-App.
 *
 * Ablauf: Haendler erzeugt hier einen Zugang und sieht das Geheimnis (`otoac_…`)
 * genau einmal. Im KI-Client traegt er nur die Server-URL ein; der Client oeffnet
 * dann oauth-authorize.html, dort gibt der Haendler das Geheimnis ein.
 *
 * Client-Namen stammen aus der offenen Dynamic Client Registration, die jeder
 * Fremde ausfuellen kann. Sie werden deshalb ausschliesslich per DOM-API und
 * textContent gesetzt, nie per innerHTML.
 */
export function renderMcp(ctx: McpSlotCtx): void {
  ctx.contentEl.hidden = false;
  ctx.contentEl.innerHTML = `
    <div class="kf-page" style="height:100%;display:flex;flex-direction:column;">
      <main class="main">
        <header class="apphead">
          <a class="brand" href="https://www.go-ecommerce.de" target="_blank" rel="noopener" title="go eCommerce">${KF_LOGO_GOECOMMERCE}</a>
          <span class="kf-logo" title="OTTO Market"><img src="/otto-market.png" alt="OTTO Market" /></span>
          <span class="hdivider"></span>
          <div class="htitle"><div class="heyebrow">OTTO · MCP</div><h1>Zugang für KI-Assistenten</h1></div>
        </header>
        <div class="scrollarea">
          <div style="max-width:720px;">
            <article class="mp-card" id="otto-mcp-card">
              <div class="mp-head">
                <span class="mp-name">Zugang für KI-Assistenten</span>
                <span class="mp-badge" id="otto-mcp-badge" hidden><span class="d"></span></span>
              </div>

              <div class="mp-sec">
                <div class="mp-lab">Server-URL</div>
                <div class="mp-field">
                  <div class="v-input"><input type="text" id="otto-mcp-url" readonly value="${MCP_SERVER_URL}" /></div>
                </div>
                <div class="mp-action">
                  <button type="button" class="mp-btn" id="otto-mcp-copy-url">
                    <span class="v-icon fa-regular fa-copy"></span>
                    Server-URL kopieren
                  </button>
                </div>
                <div class="mp-note">Mehr braucht dein KI-Client nicht. Die Anmeldung passiert danach im Browser, es wird kein Token in die Konfiguration eingetragen.</div>
              </div>

              <div class="mp-sec border">
                <div class="mp-lab">Dein Zugang</div>
                <p class="mp-hint" id="otto-mcp-zustand">Lade …</p>

                <div id="otto-mcp-anlegen" hidden>
                  <div class="mp-field">
                    <span class="mp-flab">Name</span>
                    <div class="v-input"><input type="text" id="otto-mcp-name" maxlength="60" placeholder="z. B. Team Einkauf" /></div>
                  </div>
                  <div class="mp-action">
                    <button type="button" class="mp-btn" id="otto-mcp-create">
                      <span class="v-icon fa-regular fa-plus"></span>
                      Zugang erzeugen
                    </button>
                    <span class="mp-msg err" id="otto-mcp-create-msg"></span>
                  </div>
                </div>

                <div class="mp-action" id="otto-mcp-revoke-wrap" hidden>
                  <button type="button" class="mp-btn" id="otto-mcp-revoke"></button>
                </div>
                <div class="mp-note" id="otto-mcp-revoke-note" hidden>Ein Widerruf trennt ALLE verbundenen Clients auf einmal. Um nur einen einzelnen zu lösen, unten „Trennen" benutzen.</div>

                <div class="mp-sec" id="otto-mcp-reveal" hidden>
                  <div class="mp-lab">Geheimnis, jetzt sichern</div>
                  <p class="mp-hint">Der Klartext wird aus Sicherheitsgründen nur dieses eine Mal angezeigt und danach nirgends gespeichert. Bitte jetzt kopieren. Ein verlorenes Geheimnis lässt sich nicht erneut anzeigen, nur widerrufen und neu erzeugen.</p>
                  <div class="mp-field">
                    <div class="v-input"><input type="text" id="otto-mcp-reveal-value" readonly value="" /></div>
                  </div>
                  <div class="mp-action">
                    <button type="button" class="mp-btn" id="otto-mcp-copy">
                      <span class="v-icon fa-regular fa-copy"></span>
                      Geheimnis kopieren
                    </button>
                    <button type="button" class="mp-btn" id="otto-mcp-reveal-done">
                      <span class="v-icon fa-regular fa-check"></span>
                      Fertig, Geheimnis ist gesichert
                    </button>
                  </div>
                </div>
              </div>

              <div class="mp-sec border">
                <div class="mp-lab">Verbundene Clients</div>
                <div id="otto-mcp-connections">Lade …</div>
              </div>

              <div class="mp-sec border">
                <div class="mp-lab">So verbindest du dich</div>
                <ol class="mp-hint" style="padding-left:18px;margin:0;">
                  <li>Oben einen Zugang erzeugen und das Geheimnis kopieren.</li>
                  <li>In Claude oder ChatGPT einen Connector mit der Server-URL anlegen, <strong>ohne</strong> zusätzliche Header. Ein eingetragener Header verhindert die Anmeldung.</li>
                  <li>Der Client öffnet ein Browser-Fenster. Dort das Geheimnis eingeben und freigeben.</li>
                </ol>
                <div class="mp-note">Derzeit darf ein KI-Client bei OTTO nur lesen. Ändern folgt später.</div>
              </div>
            </article>
          </div>
        </div>
      </main>
    </div>`;

  // Volle iframe-Hoehe ausnutzen, Scrolling innen (.scrollarea), wie das Log.
  const page = ctx.contentEl.querySelector<HTMLElement>('.kf-page')!;
  const fit = () => { page.style.height = `${window.innerHeight}px`; };
  window.addEventListener('resize', fit);
  window.addEventListener('orientationchange', fit);
  fit();

  void initMcp(ctx.contentEl, ctx.supabase);
}

async function initMcp(host: HTMLElement, supabase: SupabaseClient): Promise<void> {
  const root = host.querySelector<HTMLElement>('#otto-mcp-card')!;
  const q = <T extends HTMLElement>(sel: string) => root.querySelector<T>(sel)!;

  const zustandEl = q<HTMLElement>('#otto-mcp-zustand');
  const badgeEl = q<HTMLElement>('#otto-mcp-badge');
  const anlegenEl = q<HTMLElement>('#otto-mcp-anlegen');
  const nameInput = q<HTMLInputElement>('#otto-mcp-name');
  const createBtn = q<HTMLButtonElement>('#otto-mcp-create');
  const createMsgEl = q<HTMLElement>('#otto-mcp-create-msg');
  const revokeWrapEl = q<HTMLElement>('#otto-mcp-revoke-wrap');
  const revokeNoteEl = q<HTMLElement>('#otto-mcp-revoke-note');
  const revokeBtn = q<HTMLButtonElement>('#otto-mcp-revoke');
  const revealBox = q<HTMLElement>('#otto-mcp-reveal');
  const revealInput = q<HTMLInputElement>('#otto-mcp-reveal-value');
  const copyBtn = q<HTMLButtonElement>('#otto-mcp-copy');
  const revealDoneBtn = q<HTMLButtonElement>('#otto-mcp-reveal-done');
  const copyUrlBtn = q<HTMLButtonElement>('#otto-mcp-copy-url');
  const urlInput = q<HTMLInputElement>('#otto-mcp-url');
  const connEl = q<HTMLElement>('#otto-mcp-connections');

  let zugang: McpZugang | null = null;
  let verbindungen: McpVerbindung[] = [];
  // Zwei getrennte Zustaende: Zugang widerrufen und eine Verbindung trennen sind
  // verschiedene Vorgaenge. Ein gemeinsamer Zustand koennte einen scharfen Klick
  // auf den falschen Vorgang uebertragen.
  let zugangRevoke: RevokeState = createRevokeState();
  let connRevoke: RevokeState = createRevokeState();
  const laufend = new Set<string>();

  function setzeBadge(art: 'ok' | 'off', text: string): void {
    badgeEl.hidden = false;
    badgeEl.className = `mp-badge ${art}`;
    badgeEl.replaceChildren(
      Object.assign(document.createElement('span'), { className: 'd' }),
      document.createTextNode(text),
    );
  }

  function knopfInhalt(btn: HTMLButtonElement, icon: string, text: string): void {
    btn.replaceChildren(
      Object.assign(document.createElement('span'), { className: `v-icon fa-regular ${icon}` }),
      document.createTextNode(text),
    );
  }

  function zeichneZustand(): void {
    const f = formatZugang(zugang);
    if (!f.vorhanden || !f.row) {
      zustandEl.textContent = f.zustandText;
      anlegenEl.hidden = false;
      revokeWrapEl.hidden = true;
      revokeNoteEl.hidden = true;
      setzeBadge('off', 'Kein Zugang');
      return;
    }
    anlegenEl.hidden = true;
    zustandEl.textContent =
      `${f.row.name} · ${f.row.prefix}… · angelegt ${f.erstelltText} · ` +
      `zuletzt verwendet ${f.letzteNutzungText}`;
    revokeWrapEl.hidden = false;
    revokeNoteEl.hidden = false;
    knopfInhalt(revokeBtn, 'fa-ban', revokeLabel(zugangRevoke, f.row.id));
    revokeBtn.disabled = laufend.has(f.row.id);
    setzeBadge('ok', 'Aktiv');
  }

  /** Tabelle per DOM-API, Client-Namen nur per textContent (offene Registrierung, XSS). */
  function zeichneVerbindungen(): void {
    const rows = formatVerbindungen(verbindungen);
    if (rows.length === 0) {
      connEl.replaceChildren(
        Object.assign(document.createElement('p'), {
          className: 'mp-hint',
          textContent: 'Noch kein Client verbunden.',
        }),
      );
      return;
    }

    const tabelle = document.createElement('table');
    tabelle.style.width = '100%';
    const thead = document.createElement('thead');
    const kopf = document.createElement('tr');
    for (const text of ['Client', 'Umfang', 'Verbunden', 'Zuletzt erneuert', 'Zustand', '']) {
      const th = document.createElement('th');
      th.textContent = text;
      th.style.textAlign = 'left';
      kopf.appendChild(th);
    }
    thead.appendChild(kopf);
    tabelle.appendChild(thead);

    const tbody = document.createElement('tbody');
    for (const r of rows) {
      const tr = document.createElement('tr');
      for (const text of [r.anzeigeName, r.scopeText, r.verbundenText, r.zuletztText]) {
        const td = document.createElement('td');
        td.textContent = text;
        tr.appendChild(td);
      }

      const tdZustand = document.createElement('td');
      const badge = document.createElement('span');
      badge.className = r.abgelaufen ? 'mp-badge off' : 'mp-badge ok';
      badge.appendChild(Object.assign(document.createElement('span'), { className: 'd' }));
      badge.appendChild(document.createTextNode(r.zustandText));
      tdZustand.appendChild(badge);
      tr.appendChild(tdZustand);

      const tdBtn = document.createElement('td');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'mp-btn';
      knopfInhalt(btn, 'fa-link-slash', revokeLabel(connRevoke, r.id, TRENNEN_TEXTE));
      btn.disabled = laufend.has(r.id);
      btn.addEventListener('click', () => void aufTrennenKlick(r.id));
      tdBtn.appendChild(btn);
      tr.appendChild(tdBtn);
      tbody.appendChild(tr);
    }
    tabelle.appendChild(tbody);
    connEl.replaceChildren(tabelle);
  }

  async function ladeAlles(): Promise<void> {
    const { data, error } = await supabase.rpc('otto_mcp_status');
    if (error) {
      zustandEl.textContent = `Zugang konnte nicht geladen werden: ${error.message}`;
      connEl.textContent = '';
      return;
    }
    const status = parseMcpStatus(data);
    zugang = status.zugang;
    verbindungen = status.verbindungen;
    // Bei JEDEM Neuladen entschaerfen, wenn der scharfe Eintrag weg ist (Vertrag revoke-state.ts).
    zugangRevoke = syncRevokeState(zugangRevoke, zugang ? [zugang] : []);
    connRevoke = syncRevokeState(connRevoke, verbindungen);
    zeichneZustand();
    zeichneVerbindungen();
  }

  async function aufAnlegenKlick(): Promise<void> {
    const fehler = validateZugangName(nameInput.value);
    if (fehler) {
      createMsgEl.textContent = fehler;
      return;
    }
    createBtn.disabled = true;
    createMsgEl.textContent = '';
    const { data, error } = await supabase.rpc('otto_create_mcp_access', { _name: nameInput.value.trim() });
    createBtn.disabled = false;
    if (error) {
      createMsgEl.textContent = anlageFehlerText(error.message);
      await ladeAlles();
      return;
    }
    const zeile = (Array.isArray(data) ? data[0] : data) as { secret?: string } | null;
    nameInput.value = '';
    if (zeile?.secret) {
      revealInput.value = zeile.secret;
      revealBox.hidden = false;
    }
    await ladeAlles();
  }

  /** Trennt EINE Verbindung, der Zugang bleibt bestehen. */
  async function aufTrennenKlick(id: string): Promise<void> {
    if (laufend.has(id)) return;
    const ergebnis = pressRevoke(connRevoke, id);
    connRevoke = ergebnis.state;
    if (ergebnis.action !== 'revoke') {
      zeichneVerbindungen();
      return;
    }
    laufend.add(id);
    zeichneVerbindungen();
    const { error } = await supabase.rpc('otto_revoke_mcp_connection', { _id: id });
    laufend.delete(id);
    if (error) showToast(`Trennen fehlgeschlagen: ${error.message}`, 'error');
    else showToast('Verbindung getrennt', 'success');
    connRevoke = createRevokeState();
    await ladeAlles();
  }

  /** Widerruft den ZUGANG und damit alle Verbindungen. */
  async function aufWiderrufenKlick(): Promise<void> {
    const id = zugang?.id;
    if (!id || laufend.has(id)) return;
    const ergebnis = pressRevoke(zugangRevoke, id);
    zugangRevoke = ergebnis.state;
    if (ergebnis.action !== 'revoke') {
      zeichneZustand();
      return;
    }
    laufend.add(id);
    zeichneZustand();
    const { error } = await supabase.rpc('otto_revoke_mcp_access', { _id: id });
    laufend.delete(id);
    if (error) showToast(`Widerruf fehlgeschlagen: ${error.message}`, 'error');
    else showToast('Zugang widerrufen, alle Verbindungen getrennt', 'success');
    zugangRevoke = createRevokeState();
    await ladeAlles();
  }

  async function kopiere(input: HTMLInputElement, meldung: string): Promise<void> {
    input.select();
    try {
      await navigator.clipboard.writeText(input.value);
      showToast(meldung, 'success');
    } catch {
      showToast('Kopieren nicht möglich, bitte den markierten Text selbst kopieren.', 'error');
    }
  }

  copyUrlBtn.addEventListener('click', () => void kopiere(urlInput, 'Server-URL kopiert'));
  createBtn.addEventListener('click', () => void aufAnlegenKlick());
  revokeBtn.addEventListener('click', () => void aufWiderrufenKlick());
  copyBtn.addEventListener('click', () => void kopiere(revealInput, 'Geheimnis kopiert'));
  revealDoneBtn.addEventListener('click', () => {
    // Klartext aus dem DOM entfernen, nicht nur verstecken.
    revealInput.value = '';
    revealInput.removeAttribute('value');
    revealBox.hidden = true;
  });

  await ladeAlles();
}
